import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { approveReservation, fetchReservationData, getReservationById, rejectReservation } from '../api/reservationApi';
import { jwtDecode } from 'jwt-decode';
import Logo from '../componets/Logo';
import { Helmet } from 'react-helmet-async';

interface TokenPayloadDTO {
    user_id: number;
}

interface Reservation {
  id: number;
  spaceTitle: string;
  userName: string;
  depositor: {
    id: number;
    email: string;
    password: string;
    nickname: string;
    phone_number: string;
  };
  ownerId: number;
  start_date: string;
  end_date: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CONFIRMED' | 'PAYMENT_COMPLETED' | 'STORED';
  comment: string;
  createdAt: string;
  updatedAt: string;
  main_image: string | null;
  additional_images: string[] | null;
  space: {
    ownerId: number;
    id: number;
    title: string;
    description: string | null;Id: number;
    location: string;
    pricePerDay: number;
    owner: {
      id: number;
      email: string;
      nickname: string;
      phone_number?: string;
    };
  };
  total_price: number;
}

const ReservationDetail: React.FC = () => {
  const { reservationId } = useParams<{ reservationId: string }>();
  const navigate = useNavigate();
  const [reservation, setReservation] = useState<Reservation | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [storedImage, setStoredImage] = useState<File[]>([]); // 이미지 배열 관리
  const [checklist, setChecklist] = useState({
    item1: false,
    item2: false,
    item3: false,
  });
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isOwner, setIsOwner] = useState<boolean>(false);
  const [isDepositor, setIsDepositor] = useState<boolean>(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('user_token');
    if (!token) {
      alert('로그인이 필요합니다. 로그인 페이지로 이동합니다.');
      navigate('/login');
      return;
    }

    const decoded = jwtDecode<TokenPayloadDTO>(token);
    const loggedInUserId = decoded.user_id;
    
    const loadReservation = async () => {
      try {
        if (!reservationId) {
          throw new Error('Reservation ID is missing in the URL.');
        }
        const data = await getReservationById(Number(reservationId));
        setReservation(data);

        setIsOwner(loggedInUserId === data.space.owner.id);
        setIsDepositor(loggedInUserId === data.depositor.id);

      } catch (error) {
        console.error('Error fetching reservation:', error);
      } finally {
        setLoading(false);
      }
    };

    loadReservation();
  }, [reservationId, navigate]);

  if(!reservation) return <div>Loading...</div>;

  const images = [
    reservation.main_image,
    ...(reservation.additional_images || []),
  ].filter(Boolean).slice(0, 4);

  const handleTouchStart = (e: React.TouchEvent) =>
    setTouchStartX(e.changedTouches[0].clientX);
  const handleTouchEnd = (e: React.TouchEvent) => {
    const touchEndX = e.changedTouches[0].clientX;
    const distance = touchStartX - touchEndX;
    if (distance > 50 && currentImageIndex < images.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    } else if (distance < -50 && currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    }
  };

  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      setStoredImage(Array.from(files)); // 여러 파일을 상태에 배열로 저장
    }
  };

  const handleChecklistChange = (key: keyof typeof checklist) => {
    setChecklist((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleRegisterStorage = async () => {
    if (!reservation || storedImage.length === 0) return;

    const dto = {
      status: 'STORED',
      stored_image: storedImage, // 여러 이미지를 'stored_image'로 전달
    };

    try {
      // 서버로 사진과 예약 상태 정보를 전송
      await fetchReservationData(reservation.id, dto);

      setReservation({ ...reservation, status: 'STORED' });
      setIsModalOpen(false); // 모달 닫기
    } catch (error) {
      console.error('Error registering storage:', error);
    }
  };

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const isStorageRegistrationDisabled =
    storedImage.length === 0 || !checklist.item1 || !checklist.item2 || !checklist.item3;

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!reservation) {
    return <div>No reservation found.</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white shadow-md rounded-md">
      <Helmet>
        <title>예약 상세 | BIN-GO</title>
        <meta name="description" content="예약한 상세 내역을 확인하세요." />
        <meta property="og:title" content="예약 상세 | BIN-GO" />
        <meta property="og:description" content="공간 예약 상세 페이지." />
      </Helmet>
      <Logo />
      <h1 className="text-2xl font-bold mb-4 mt-6">물건 보관 요청서</h1>
        {/* <p>사진: {reservation.main_image ? <img src={reservation.main_image} alt="Main" /> : 'No image available'}</p> */}
      
      
      <div className="border border-gray-300 rounded p-4">
        <h2 className="text-lg font-semibold">보관 장소: {reservation.space?.owner?.nickname}님 {reservation.space?.title}</h2>
        {images.length > 0 && (
        <div
          className="relative w-full h-64 mb-4 overflow-hidden rounded-lg"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <img
            src={images[currentImageIndex]!}
            alt={`reservation ${currentImageIndex}`}
            className="w-full h-full object-cover"
          />
          {/* dot 인디케이터 */}
          <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-2">
            {images.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentImageIndex(idx)}
                className={`w-2 h-2 rounded-full ${
                  currentImageIndex === idx ? 'bg-blue-500' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>
      )}
        <p>
          보관일: {reservation.start_date} - {reservation.end_date}
        </p>
        <p>물건 상태: {reservation.status}</p>
        <p>요금: {reservation.total_price.toLocaleString()} (/일)</p>
        <p>장소: {reservation.space?.location}</p>

        {reservation?.status === 'PAYMENT_COMPLETED' && (
          <div className="mt-6">
            <button
              onClick={openModal}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              물건 보관 등록
            </button>
          </div>
        )}

        {reservation.status === 'PENDING' && isOwner && (
          <div className="mt-6 space-x-4">
            <button
              onClick={async () => {
                try {
                  // 승인 처리 API 호출
                  await approveReservation(reservation.id);
                  setReservation({ ...reservation, status: 'APPROVED' });
                } catch (err) {
                  console.error('Error approving reservation:', err);
                }
              }}
              className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
            >
              승인
            </button>
            <button
              onClick={async () => {
                try {
                  // 거절 처리 API 호출
                  await rejectReservation(reservation.id);
                  setReservation({ ...reservation, status: 'REJECTED' });
                } catch (err) {
                  console.error('Error rejecting reservation:', err);
                }
              }}
              className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
            >
              거절
            </button>
          </div>
        )}

        {reservation.status === 'PENDING' && isDepositor && (
          <div className="mt-6 space-x-4">
            <button
              onClick={async () => {
                try {
                  // 예약 상태를 REJECTED로 변경하는 데이터 전송
                  const dto = {
                    status: 'REJECTED',
                  };
              
                  // fetchReservationData 호출하여 예약 상태를 REJECTED로 업데이트
                  const updatedReservation = await fetchReservationData(reservation.id, dto);
                  
                  // 예약 상태 업데이트
                  setReservation({ ...reservation, status: 'REJECTED' });
              
                  // /spaces 페이지로 이동
                  navigate('/spaces');
                } catch (err) {
                  console.error('Error rejecting reservation:', err);
                }
              }}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              예약 취소
            </button>

            <button
              onClick={async () => {
                navigate('/spaces')
              }}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              확인
            </button>
          </div>
        )}
      </div>

      {/* 모달 */}
      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded shadow-lg w-96">
            <h3 className="text-xl font-semibold mb-4">Storage Registration</h3>
            <div className="mb-4">
              <label className="block font-medium mb-2">Upload Photos:</label>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handlePhotoUpload}
                className="block w-full"
              />
            </div>
            <div className="mb-4">
              <label className="block font-medium mb-2">Checklist:</label>
              <div>
                <label className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    checked={checklist.item1}
                    onChange={() => handleChecklistChange('item1')}
                    className="mr-2"
                  />
                  사진 속 물건이 예약서 내 물건과 일치하는지 확인하셨나요?
                </label>
                <label className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    checked={checklist.item2}
                    onChange={() => handleChecklistChange('item2')}
                    className="mr-2"
                  />
                  보안 씰 체크 여부를 확인하셨나요?
                </label>
                <label className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    checked={checklist.item3}
                    onChange={() => handleChecklistChange('item3')}
                    className="mr-2"
                  />
                  물품의 상태를 확인하셨나요?
                </label>
              </div>
            </div>
            <button
              onClick={handleRegisterStorage}
              disabled={isStorageRegistrationDisabled}
              className={`px-4 py-2 rounded ${
                isStorageRegistrationDisabled
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              확인
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReservationDetail;
